import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface Category {
  id: number;
  name: string;
  subcategories: Subcategory[];
}

interface Subcategory {
  id: number;
  name: string;
}

interface CategoryOption {
  categoryId: number;
  subcategoryId: number | null;
  categoryName: string;
  subcategoryName: string | null;
  displayText: string;
  searchText: string;
}

interface Props {
  categories: Category[];
  currentCategoryId: number | null;
  currentSubcategoryId: number | null;
  onChange: (categoryId: number | null, subcategoryId: number | null) => void;
  isIncome: boolean;
  isRefund?: boolean;
  placeholder?: string;
  className?: string;
}

export const CategorySubcategorySelect: React.FC<Props> = ({
  categories,
  currentCategoryId,
  currentSubcategoryId,
  onChange,
  isIncome,
  isRefund = false,
  placeholder,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [openUpward, setOpenUpward] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<(HTMLDivElement | null)[]>([]);

  // Determine the appropriate placeholder based on current state
  const getPlaceholder = () => {
    if (placeholder) return placeholder; // Use custom placeholder if provided
    
    if (!currentCategoryId) {
      return isIncome ? "Uncategorized income" : "Uncategorized expense";
    }
    
    return isIncome ? "Search income categories..." : "Search expense categories...";
  };

  // Build all possible category + subcategory combinations
  const allOptions: CategoryOption[] = React.useMemo(() => {
    const options: CategoryOption[] = [];
    
    // Add special options first
    options.push({
      categoryId: -1,
      subcategoryId: null,
      categoryName: 'Clear',
      subcategoryName: null,
      displayText: '🗑️ Clear Category',
      searchText: 'clear category remove delete'
    });

    options.push({
      categoryId: -2,
      subcategoryId: null,
      categoryName: 'Transfer',
      subcategoryName: null,
      displayText: isIncome ? '🔄 Transfer from Bank/Credit Card/Loan' : '🔄 Transfer to Bank/Credit Card/Loan',
      searchText: 'transfer bank credit card loan'
    });

    if (isIncome) {
      options.push({
        categoryId: -3,
        subcategoryId: null,
        categoryName: 'Refund',
        subcategoryName: null,
        displayText: '💰 Refund for Expense',
        searchText: 'refund expense money back'
      });
    }

    // Add category + subcategory combinations
    categories.forEach(category => {
      if (category.subcategories && category.subcategories.length > 0) {
        // Add category + subcategory combinations
        category.subcategories.forEach(subcategory => {
          options.push({
            categoryId: category.id,
            subcategoryId: subcategory.id,
            categoryName: category.name,
            subcategoryName: subcategory.name,
            displayText: `${category.name} - ${subcategory.name}`,
            searchText: `${category.name} ${subcategory.name}`.toLowerCase()
          });
        });
      } else {
        // Add category without subcategories (e.g., "Insurance Reimbursement")
        options.push({
          categoryId: category.id,
          subcategoryId: null,
          categoryName: category.name,
          subcategoryName: null,
          displayText: category.name,
          searchText: category.name.toLowerCase()
        });
      }
    });

    return options;
  }, [categories, isIncome]);

  // Filter options based on search term
  const filteredOptions = React.useMemo(() => {
    if (!searchTerm.trim()) {
      return allOptions;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    return allOptions.filter(option => 
      option.searchText.includes(searchLower)
    );
  }, [allOptions, searchTerm]);

  // Get current display value
  const getCurrentDisplayValue = () => {
    if (!currentCategoryId) return '';
    
    // Check if this is a transfer (we need to check if the transaction is marked as transfer)
    // For now, we'll look for the Transfer category by name
    const transferCategory = categories.find(cat => cat.name === 'Transfer');
    if (transferCategory && currentCategoryId === transferCategory.id) {
      return isIncome ? '🔄 Transfer from Bank/Credit Card/Loan' : '🔄 Transfer to Bank/Credit Card/Loan';
    }
    
    // Check if this is a refund transaction
    if (isRefund) {
      const option = allOptions.find(opt => 
        opt.categoryId === currentCategoryId && opt.subcategoryId === currentSubcategoryId
      );
      if (option) {
        return `💰 Refund of ${option.displayText}`;
      }
    }
    
    const option = allOptions.find(opt =>
      opt.categoryId === currentCategoryId &&
      (opt.subcategoryId ?? null) === (currentSubcategoryId ?? null)
    );
    
    return option ? option.displayText : '';
  };

  // Handle option selection
  const selectOption = (option: CategoryOption) => {
    if (option.categoryId === -1) {
      // Clear category
      onChange(null, null);
    } else if (option.categoryId === -2) {
      // Transfer - handle specially
      onChange(-2, null); // The parent will handle transfer logic
    } else if (option.categoryId === -3) {
      // Refund - handle specially  
      onChange(-3, null); // The parent will handle refund logic
    } else {
      // Regular category/subcategory
      onChange(option.categoryId, option.subcategoryId ?? null);
    }
    
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        setIsOpen(true);
        setHighlightedIndex(0);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          selectOption(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && optionsRef.current[highlightedIndex]) {
      optionsRef.current[highlightedIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [highlightedIndex]);

  // Calculate dropdown position for portal rendering
  const calculateDropdownPosition = () => {
    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 240; // max-height from CSS
      
      // Calculate position
      const top = rect.bottom;
      const left = rect.left;
      const width = rect.width;
      
      // If there's not enough space below, open upward
      const shouldOpenUpward = rect.bottom + dropdownHeight > viewportHeight - 20; // 20px margin
      setOpenUpward(shouldOpenUpward);
      
      // Set position for portal
      setDropdownPosition({
        top: shouldOpenUpward ? rect.top - dropdownHeight : top,
        left: left,
        width: width
      });
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Check if click is outside both the input container and the dropdown
      const isOutsideInput = dropdownRef.current && !dropdownRef.current.contains(target);
      const isOutsideDropdown = !(target as Element).closest('.options-dropdown');
      
      if (isOutsideInput && isOutsideDropdown) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Calculate position when opening dropdown
  useEffect(() => {
    if (isOpen) {
      calculateDropdownPosition();
    }
  }, [isOpen]);

  // Recalculate position on scroll
  useEffect(() => {
    if (isOpen) {
      const handleScroll = () => calculateDropdownPosition();
      window.addEventListener('scroll', handleScroll, true);
      return () => window.removeEventListener('scroll', handleScroll, true);
    }
  }, [isOpen]);

  // Recalculate position when search term changes (for filtered results)
  useEffect(() => {
    if (isOpen) {
      // Small delay to allow DOM to update with filtered results
      const timeoutId = setTimeout(() => {
        calculateDropdownPosition();
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm, isOpen]);

  return (
    <div className={`category-subcategory-select ${className}`} ref={dropdownRef}>
      <div className="select-container">
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? searchTerm : getCurrentDisplayValue()}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setHighlightedIndex(0);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => {
            setIsOpen(true);
            setSearchTerm('');
          }}
          onKeyDown={handleKeyDown}
          placeholder={getPlaceholder()}
          className="select-input"
          autoComplete="off"
        />
        <div 
          className="select-arrow"
          onClick={() => {
            setIsOpen(!isOpen);
            if (!isOpen) {
              inputRef.current?.focus();
              setSearchTerm('');
            }
          }}
        >
          <svg 
            className={`arrow-icon ${isOpen ? 'open' : ''}`} 
            viewBox="0 0 20 20" 
            fill="currentColor"
          >
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      </div>

      {isOpen && createPortal(
        <div 
          className={`options-dropdown ${openUpward ? 'open-upward' : ''}`}
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            zIndex: 9999
          }}
        >
          {filteredOptions.length === 0 ? (
            <div className="no-options">
              No matching categories found
            </div>
          ) : (
            filteredOptions.map((option, index) => (
              <div
                key={`${option.categoryId}-${option.subcategoryId}`}
                ref={el => optionsRef.current[index] = el}
                className={`option ${index === highlightedIndex ? 'highlighted' : ''} ${option.categoryId === -1 ? 'clear-option' : ''}`}
                onClick={() => selectOption(option)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                {option.displayText}
              </div>
            ))
          )}
        </div>,
        document.body
      )}
    </div>
  );
};
